import { Component } from '@angular/core';
import { AnyoTableComponent } from 'projects/app-core/src/app/models/AnyoTableComponent';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { PhysicalFitnessService } from '../../../services/physical-fitness.service';
import { FitnessLogsGridItem, FitnessLogsGridResponseData } from '../../../models/fitness/fitnessLogsGridResponse';
import { FitnessLogsDateFields, FitnessLogsFilterFields, FitnessLogsGridRequest, FitnessLogsSortFields } from '../../../models/fitness/fitnessLogsGridRequest';
import { AnyoSortDirection } from 'projects/app-core/src/models/gridRequest';
import { AuthService } from 'projects/app-core/src/auth/auth.service';
import { IAnyoUser } from 'projects/app-core/src/auth/models/IAnyoUser';
import { IAnyoError } from 'projects/app-core/src/models/errorModal';
import { FileUtilsService } from 'projects/app-core/src/service/fileService';

interface CheckBoxOptions {
    label: string;
    value: string;
    checked: boolean;
}

@Component({
    selector: 'app-fitness-logs',
    templateUrl: './fitness-logs.component.html',
    styleUrls: ['./fitness-logs.component.scss']
})
export class FitnessLogsComponent extends AnyoTableComponent<FitnessLogsGridResponseData, FitnessLogsGridRequest> {
    // Filter options
    eventsCheckBoxOptions: CheckBoxOptions[] = [];
    statusCheckBoxOptions: CheckBoxOptions[] = [];
    partnerIdCheckBoxOptions: CheckBoxOptions[] = [];
    titleCheckBoxOptions: CheckBoxOptions[] = [];
    showTestBookings = false;
    globalSearchInput = "";
    dateRange: Date[] | null = null;

    // For template access
    FitnessLogsSortFields = FitnessLogsSortFields;
    tableData: FitnessLogsGridItem[] = [];
    tableQuery: FitnessLogsGridRequest = {} as FitnessLogsGridRequest;
    loading = false;
    totalRecords = 0;

    constructor(
        private fitnessService: PhysicalFitnessService,
        protected override auth: AuthService,
        protected override toastService: ToastService,
        private fileUtils: FileUtilsService
    ) {
        super(auth, toastService);
    }

    globalSearch(){
        if (this.globalSearchInput.length) {
            this.gridRequest?.globalSearch?.push(this.globalSearchInput.trim());
            this.getData();
          }
    }

    // Implement abstract method required by AnyoTableComponent
    override async getData(): Promise<void> {
        this.loading = true;
        this.tableData = [];
        this.tableQuery = this.gridRequest as FitnessLogsGridRequest;

        try {
            const response = await this.fitnessService.fetchFitnessLogsGrid(this.gridRequest as FitnessLogsGridRequest).toPromise();

            if (response) {
                this.data = response.data;
                this.tableData = response.data;
                this.totalCount = response.totalRecords;
                this.totalRecords = response.totalRecords;
                this.updateFilterOptions(response.filterOptions);
            }
        } catch (error: unknown) {
            const errorBody = error as { error: IAnyoError };
            this.toastService.showAnyoErrorToast(errorBody.error.description);
        } finally {
            this.loading = false;
        }
    }

    // Override ready to initialize the component
    override async ready(user?: IAnyoUser): Promise<void> {
        if (user) {
            this.initializeGridRequest();
            await this.getData();
        }
        return Promise.resolve();
    }

    initializeGridRequest(): void {
        this.gridRequest = {
            page: this.currentPageNo,
            pageSize: this.pageSize,
            globalSearch: [],
            export: false,
            filters: {
                [FitnessLogsFilterFields.HIDE_TEST_BOOKING]: ["true"],
            } as Record<FitnessLogsFilterFields, string[] | undefined>,
        } as FitnessLogsGridRequest;
    }

    updateFilterOptions(filterOptions: { events: string[]; status: string[]; partnerId: string[]; titles: string[] }): void {
        // Update event filter options
        this.eventsCheckBoxOptions = filterOptions.events.map((event) => ({
            label: event,
            value: event,
            checked: this.isFilterSelected(FitnessLogsFilterFields.EVENTS, event),
        }));

        // Update status filter options
        this.statusCheckBoxOptions = filterOptions.status.map((status) => ({
            label: status,
            value: status,
            checked: this.isFilterSelected(FitnessLogsFilterFields.STATUS, status),
        }));

        // Update partner filter options
        this.partnerIdCheckBoxOptions = filterOptions.partnerId.map((partnerId) => ({
            label: partnerId,
            value: partnerId,
            checked: this.isFilterSelected(FitnessLogsFilterFields.PARTNER_ID, partnerId),
        }));

        // Update title filter options
        this.titleCheckBoxOptions = filterOptions.titles.map((title) => ({
            label: title,
            value: title,
            checked: this.isFilterSelected(FitnessLogsFilterFields.TITLE, title),
        }));
    }

    isFilterSelected(filterField: FitnessLogsFilterFields, value: string): boolean {
        return !!this.gridRequest.filters?.[filterField]?.includes(value);
    }

    // Filter handlers
    applyEventsFilter(events: string[]): void {
        if (!this.gridRequest.filters) {
            this.gridRequest.filters = {} as Record<FitnessLogsFilterFields, string[] | undefined>;
        }
        this.gridRequest.filters[FitnessLogsFilterFields.EVENTS] = events;
        this.getData();
    }

    applyStatusFilter(statuses: string[]): void {
        if (!this.gridRequest.filters) {
            this.gridRequest.filters = {} as Record<FitnessLogsFilterFields, string[] | undefined>;
        }
        this.gridRequest.filters[FitnessLogsFilterFields.STATUS] = statuses;
        this.getData();
    }

    applyPartnerFilter(partners: string[]): void {
        console.log(partners);
        if (!this.gridRequest.filters) {
            this.gridRequest.filters = {} as Record<FitnessLogsFilterFields, string[] | undefined>;
        }
        this.gridRequest.filters[FitnessLogsFilterFields.PARTNER_ID] = partners;
        this.getData();
    }

    applyTitleFilter(titles: string[]): void {
        if (!this.gridRequest.filters) {
            this.gridRequest.filters = {} as Record<FitnessLogsFilterFields, string[] | undefined>;
        }
        this.gridRequest.filters[FitnessLogsFilterFields.TITLE] = titles;
        this.getData();
    }

    applyDateFilter(): void {
        if (!this.dateRange || this.dateRange.length !== 2) {
            return;
        }

        if (!this.gridRequest.dateFilters) {
            this.gridRequest.dateFilters = {};
        }

        this.gridRequest.dateFilters[FitnessLogsDateFields.CREATED_AT] = {
            from: this.dateRange[0],
            to: this.dateRange[1],
        };

        this.getData();
    }

    applyGlobalSearch(): void {
        if (this.globalSearchInput.trim()) {
            this.gridRequest.globalSearch = [this.globalSearchInput.trim()];
            this.getData();
        }
    }

    toggleTestBookings(): void {
        if (!this.gridRequest.filters) {
            this.gridRequest.filters = {} as Record<FitnessLogsFilterFields, string[] | undefined>;
        }
        this.gridRequest.filters[FitnessLogsFilterFields.HIDE_TEST_BOOKING] = this.showTestBookings ? ["false"] : ["true"];
        this.getData();
    }

    // Sort handlers
    applySorting(field: FitnessLogsSortFields, direction: AnyoSortDirection | null): void {
        if (!this.gridRequest.sort) {
            this.gridRequest.sort = {};
        }

        if (direction === null) {
            delete this.gridRequest.sort[field];
        } else {
            this.gridRequest.sort[field] = direction;
        }

        this.getData();
    }

    // Pagination handlers
    onPageSizeChange(size: number): void {
        this.gridRequest.pageSize = size;
        this.getData();
    }

    onPageChange(page: number): void {
        this.gridRequest.page = page;
        this.getData();
    }

    // Reset filters
    resetFilters(): void {
        this.initializeGridRequest();
        this.globalSearchInput = "";
        this.dateRange = null;
        this.getData();
    }

    // Export data
    exportData(): void {
        const exportQuery = { ...this.gridRequest, export: true } as FitnessLogsGridRequest;
        this.fitnessService.fetchFitnessLogsGrid(exportQuery).subscribe({
            next: (value) => {
                this.toastService.showSuccess("Export initiated successfully");
                this.fileUtils.exportCSV(value.data,"fitness-logs");
            },
            error: (error: { error: IAnyoError }) => {
                this.toastService.showAnyoErrorToast(error.error.description);
            },
        });
    }

    // Helpers
    getActivityType(item: FitnessLogsGridItem): string {
        if (item.workoutName) return "Workout";
        if (item.planName) return "Plan";
        if (item.challengeName) return "Challenge";
        return "Unknown";
    }

    getActivityName(item: FitnessLogsGridItem): string {
        return item.workoutName || item.planName || item.challengeName || "N/A";
    }

    // Helper method to convert nz-table sort direction to AnyoSortDirection
    convertSortDirection(direction: string | null): AnyoSortDirection | null {
        if (!direction) {
            return null;
        }
        return direction === 'ascend' ? AnyoSortDirection.ASC : AnyoSortDirection.DESC;
    }

    // Direct sort handler for table
    sortByColumn(field: FitnessLogsSortFields, direction: string | null): void {
        this.applySorting(field, this.convertSortDirection(direction));
    }

    // Helper method to extract checked values from checkbox options
    getCheckedValues(options: CheckBoxOptions[]): string[] {
        return options.filter(o => o.checked).map(o => o.value);
    }
} 